import React, { Component } from 'react';
import { isEmpty, isEqual, isNil } from 'lodash';
import Overlay from 'src/common-ui/components/Overlay/Overlay';
import styleEditStyles from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.styles';
import { generateStylePreview } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import Subheader from 'src/components/Subheader/Subheader.container';
import { formatSummaries } from 'src/utils/Pivot/RollUp';
import CompanionList from 'src/components/CompanionList/CompanionList';
import type { StyleEditProps, StyleEditState } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.types';
import StylePreview from 'src/components/StylePreview/StylePreview';
import { FabType } from 'src/components/higherOrder/withFab';
import { SortByDirection } from 'src/components/Subheader/Subheader.types';
import { parseCompanionListViewConfig } from 'src/utils/Component/ListView';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { companionDataParse } from 'src/components/ListGridPair/ListGridPair.utils';
import { NoSelectedCompanionItemOverlay } from 'src/components/NoSelectedCompanionItemOverlay/NoSelectedCompanionItemOverlay';

export default class StyleEdit extends Component<StyleEditProps, StyleEditState> {
  constructor(props: StyleEditProps) {
    super(props);

    this.state = {
      companionScrollTo: this.props.redirectSelection && {
        eventId: Date.now(),
        where: {
          key: 'id',
          value: this.props.redirectSelection,
        },
      },
      selectedCompanionId: this.props.redirectSelection?.id ?? null,
      sortByDirection: 'desc',
      sortByDataIndex: '',
    };
  }

  componentDidMount() {
    this.props.onShowView();
    this.setupDefaultSortBy();
  }

  componentDidUpdate(prevProps: StyleEditProps) {
    // `generateCompanionData` needs config to be present or else it will crash
    if (!prevProps.redirectSelection && this.props.redirectSelection && !isEmpty(this.props.config)) {
      const { companionData } = this.generateCompanionData();
      const selectedCompanionItem = companionData.find((s) => s.id === this.props.redirectSelection?.id);
      this.setState({
        selectedCompanionId: selectedCompanionItem?.id ?? null,
        companionScrollTo: {
          eventId: Date.now(),
          where: {
            key: 'id',
            value: this.props.redirectSelection.id,
          },
        },
      });
    }

    if (!isEqual(this.props.config, prevProps.config)) {
      this.setupDefaultSortBy();
    }
  }

  componentWillUnmount(): void {
    this.props.onDestroy();
  }

  setupDefaultSortBy() {
    if (isEmpty(this.state.sortByDataIndex)) {
      this.setState({
        sortByDataIndex: this.props.config.companionView?.sortBy.defaults.dataIndex,
      });
    }
  }

  generateCompanionData = () => {
    const { config, styles } = this.props;
    const { sortByDataIndex, sortByDirection } = this.state;
    const companionDataLookup = parseCompanionListViewConfig(
      config.companionView.itemMappings as unknown as TenantConfigViewData,
    );
    const companionData = companionDataParse(styles, companionDataLookup, sortByDirection, sortByDataIndex);
    return {
      companionData,
      companionDataLookup,
    };
  };

  onFabClick = () => {
    switch (this.props.fab.fabType) {
      case FabType.planning:
        this.props.updateAssortmentPlan();
        break;
      default:
        break;
    }
  };

  handleItemSelect = (companionItemId: string) => {
    if (companionItemId === '') {
      this.setState({ selectedCompanionId: null });
      return;
    }
    if (isEqual(companionItemId, this.state.selectedCompanionId)) {
      this.setState(
        {
          selectedCompanionId: null,
        },
        () => {
          this.setState({
            selectedCompanionId: companionItemId,
            companionScrollTo: {
              eventId: Date.now(),
              where: {
                key: 'id',
                value: companionItemId,
              },
            },
          });
        },
      );
    } else {
      this.setState({
        selectedCompanionId: companionItemId,
        companionScrollTo: {
          eventId: Date.now(),
          where: {
            key: 'id',
            value: companionItemId,
          },
        },
      });
    }
  };

  handleSortChange = (sort: string) => {
    this.setState({
      sortByDataIndex: sort,
    });
  };

  handleSortDirectionChange = (direction: SortByDirection) => {
    this.setState({
      sortByDirection: direction,
    });
  };

  render() {
    // config is required to render the rest of the component chain
    // so render only overlay until config is ready
    if (isEmpty(this.props.config) && this.props.isLoading) {
      return <Overlay type="loading" visible={true} qaKey="StyleEditOverlay" />;
    }

    const {
      title,
      isLoading,
      config,
      adornments,
      summary,
      companionFilters,
      previewFilters,
      flowStatusOptions,
      searchKeys,
      sortByDefn,
      isCompanionCollapsed,
      onSearchChange,
      onFilterChange,
      onShowView,
      onUpdate,
      onError,
      onToggleCompanion,
    } = this.props;
    const { companionScrollTo, selectedCompanionId, sortByDataIndex, sortByDirection } = this.state;

    const { companionData, companionDataLookup } = this.generateCompanionData();
    const selectedCompanionItem = companionData.find((item) => item.id === selectedCompanionId) ?? null;
    const stylePreviewData = selectedCompanionItem ? generateStylePreview(selectedCompanionItem) : null;
    const subheaderProps = {
      title,
      sortByDefn: !isNil(sortByDefn) ? sortByDefn : 'AssortmentBuildStyleEditSortBy',
      summary: formatSummaries(summary),
      showAlternateFlowStatus: true,
      showAlternateSearch: true,
      sortByRequired: true,
    };
    let mainViewComponent: JSX.Element;
    if (stylePreviewData) {
      mainViewComponent = (
        <StylePreview
          adornments={adornments}
          filters={previewFilters}
          searchKeys={searchKeys}
          previewData={stylePreviewData}
          renderSections={true}
          sectionsViewDefns={config.sectionsViewDefns}
          onRefreshView={onShowView}
          onStyleItemEdit={onUpdate}
          onError={onError}
        />
      );
    } else if (!isLoading) {
      // Show no selected if there is no selected item and view is finished loading
      mainViewComponent = <NoSelectedCompanionItemOverlay />;
    } else {
      // show empty div while view is loading for cleanliness
      mainViewComponent = <div />;
    }
    return (
      <div className={styleEditStyles.styleEditContainer}>
        <Overlay type="loading" visible={isLoading} />
        <Subheader {...subheaderProps} />
        <div className="data-container">
          <CompanionList
            isCompanionCollapsed={isCompanionCollapsed}
            config={config.companionView}
            selectedItemId={selectedCompanionId}
            data={companionData}
            dataLookup={companionDataLookup}
            isDataloaded={!isLoading}
            renderSearchComponent={true}
            renderFilterComponent={true}
            sortDirection={sortByDirection}
            sortField={sortByDataIndex}
            search={companionFilters.search}
            flowStatus={companionFilters.flowStatus}
            flowStatusOptions={flowStatusOptions}
            onSearchChange={onSearchChange}
            onFilterChange={onFilterChange}
            onSortChange={this.handleSortChange}
            onSortDirectionChange={this.handleSortDirectionChange}
            onItemSelect={this.handleItemSelect}
            onToggleCompanion={onToggleCompanion}
            scrollTo={companionScrollTo}
          />
          <div
            style={{ height: '100%', overflowX: 'auto', flexGrow: 1 }}
            id={'StyleEditStylePreviewScroller'}
            data-qa="StyleEditStylePreviewScroller"
          >
            {mainViewComponent}
          </div>
        </div>
      </div>
    );
  }
}
